import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import clsx from "clsx";

import Button from "@/components/Base/Button";
import Alert from "@/components/Base/Alert";
import Lucide from "@/components/Base/Lucide";
import FormInput from "@/components/Base/Form/FormInput";
import FormSelect from "@/components/Base/Form/FormSelect";
import { getToken } from "@/utils/auth";

type Mode = "add" | "edit";

interface Props {
  /** Optional override. If not provided, will auto-detect from route param `id` */
  mode?: Mode;
}

interface Region {
  id: string;
  name: string;
  companyId: string;
}

interface Team {
  id: string;
  name: string;
}

interface FormData {
  name: string;
  code: string;
  regionId: string;
  supplyTo: string[];
  status: string;
  companyId: string;
  projectId: string;
}

interface FormErrors {
  name?: string;
  regionId?: string;
  supplyTo?: string;
}

const EMPTY_FORM: FormData = {
  name: "",
  code: "",
  regionId: "",
  supplyTo: [],
  status: "true",
  companyId: "",
  projectId: "",
};

export default function EditDistributor({ mode: modeProp }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [regions, setRegions] = useState<Region[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  // Auto detect mode from URL param
  const mode = modeProp || (id ? "edit" : "add");

  useEffect(() => {
    const init = async () => {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      try {
        setLoading(true);
        setSubmitError("");
        setSubmitSuccess("");

        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");
        const projectId = urlParams.get("projectId");

        // Regions
        let loadedRegions: Region[] = [];
        try {
          const regionsUrl = companyId ? `${apiUrl}/regions?companyId=${companyId}` : `${apiUrl}/regions`;
          const regionsRes = await axios.get(regionsUrl, { headers: { Authorization: `Bearer ${token}` } });
          loadedRegions = regionsRes.data?.data || [];
          setRegions(loadedRegions);
        } catch {
          setRegions([]);
        }

        // Stocks Supply To comes from Team.name (multi-select)
        try {
          const queryParams = new URLSearchParams();
          if (companyId) queryParams.append("companyId", companyId);
          if (projectId) queryParams.append("projectId", projectId);
          queryParams.append("page", "1");
          queryParams.append("pageSize", "500");
          const teamsRes = await axios.get(`${apiUrl}/teams?${queryParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setTeams(teamsRes.data?.data || []);
        } catch {
          setTeams([]);
        }

        // Pre-fill companyId / projectId and load distributor if editing
        setFormData((prev) => ({
          ...prev,
          companyId: companyId || prev.companyId,
          projectId: projectId || prev.projectId,
        }));

        if (mode === "edit" && id) {
          const distributorRes = await axios.get(`${apiUrl}/distributors/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const d = distributorRes.data as {
            id: string;
            name: string;
            code: string;
            region: string;
            supplyTo: string[];
            status: boolean;
          };

          const matchingRegion = loadedRegions.find((r) => r.name === d.region);

          setFormData((prev) => ({
            ...prev,
            name: d.name || "",
            code: d.code || "",
            regionId: matchingRegion?.id || prev.regionId,
            supplyTo: Array.isArray(d.supplyTo) ? d.supplyTo : [],
            status: d.status ? "true" : "false",
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [mode, id]);

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Distributor name is required";
    }

    if (!formData.regionId.trim()) {
      newErrors.regionId = "Please select region";
    }

    if (!Array.isArray(formData.supplyTo) || formData.supplyTo.length === 0) {
      newErrors.supplyTo = "Please select Stocks Supply To";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      const payload = {
        name: formData.name,
        code: formData.code,
        regionId: formData.regionId,
        supplyTo: formData.supplyTo,
        status: formData.status === "true",
      };

      if (mode === "add") {
        await axios.post(`${apiUrl}/distributors`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmitSuccess("Distributor created successfully!");
      } else {
        await axios.put(`${apiUrl}/distributors/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmitSuccess("Distributor updated successfully!");
      }

      setTimeout(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const projectId = searchParams.get("projectId");
        const companyId = formData.companyId;

        let redirectUrl = "/distributors";
        if (projectId) {
          redirectUrl += `?projectId=${projectId}`;
        } else if (companyId) {
          redirectUrl += `?companyId=${companyId}`;
        }

        navigate(redirectUrl);
      }, 900);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Failed to save distributor";
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const projectId = searchParams.get("projectId");
    const companyId = formData.companyId;

    let redirectUrl = "/distributors";
    if (projectId) {
      redirectUrl += `?projectId=${projectId}`;
    } else if (companyId) {
      redirectUrl += `?companyId=${companyId}`;
    }

    navigate(redirectUrl);
  };

  return (
    <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
      <div className="col-span-12">
        <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
          <div className="text-base font-medium group-[.mode--light]:text-white">
            {mode === "add" ? "Add Distributor" : "Edit Distributor"}
          </div>
        </div>

        <div className="mt-3.5">
          <div className="flex flex-col box box--stacked max-w-full overflow-hidden">
            <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
              <div className="flex-1">
                <form onSubmit={handleSubmit}>
                  <div className="p-5 border rounded-[0.6rem] border-slate-200/60 dark:border-darkmode-400">
                    <div className="flex items-center pb-5 text-[0.94rem] font-medium border-b border-slate-200/60 dark:border-darkmode-400">
                      <Lucide icon="ChevronDown" className="w-5 h-5 stroke-[1.3] mr-2" />
                      Distributor Information
                    </div>

                    {submitError && (
                      <Alert variant="outline-danger" className="mt-5">
                        <Lucide icon="AlertTriangle" className="w-6 h-6" />
                        {submitError}
                      </Alert>
                    )}

                    {submitSuccess && (
                      <Alert variant="outline-success" className="mt-5">
                        <Lucide icon="CheckCircle" className="w-6 h-6" />
                        {submitSuccess}
                      </Alert>
                    )}

                    <div className="mt-5 grid grid-cols-12 gap-4">
                      <div className="col-span-12 md:col-span-6">
                        <label className="inline-block mb-2">
                          Distributor Name <span className="text-danger">*</span>
                        </label>
                        <FormInput
                          type="text"
                          placeholder="Enter distributor name"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          className={clsx({ "border-danger": errors.name })}
                        />
                        {errors.name && <div className="mt-2 text-danger text-xs">{errors.name}</div>}
                      </div>

                      <div className="col-span-12 md:col-span-6">
                        <label className="inline-block mb-2">Distributor Code</label>
                        <FormInput
                          type="text"
                          placeholder="Enter distributor code"
                          value={formData.code}
                          onChange={(e) => handleInputChange("code", e.target.value)}
                        />
                      </div>

                      <div className="col-span-12 md:col-span-6">
                        <label className="inline-block mb-2">
                          Region <span className="text-danger">*</span>
                        </label>
                        <FormSelect
                          value={formData.regionId}
                          onChange={(e) => handleInputChange("regionId", e.target.value)}
                          className={clsx({ "border-danger": errors.regionId })}
                        >
                          <option value="">Select a region</option>
                          {regions.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </FormSelect>
                        {errors.regionId && <div className="mt-2 text-danger text-xs">{errors.regionId}</div>}
                      </div>

                      <div className="col-span-12 md:col-span-6">
                        <label className="inline-block mb-2">
                          Stocks Supply To <span className="text-danger">*</span>
                        </label>
                        <FormSelect
                          multiple
                          value={formData.supplyTo}
                          onChange={(e) => {
                            const values = Array.from(e.currentTarget.selectedOptions).map((o) => o.value);
                            handleInputChange("supplyTo", values);
                          }}
                          className={clsx({ "border-danger": errors.supplyTo })}
                        >
                          {teams.map((t) => (
                            <option key={t.id} value={t.name}>
                              {t.name}
                            </option>
                          ))}
                        </FormSelect>
                        {errors.supplyTo && <div className="mt-2 text-danger text-xs">{errors.supplyTo}</div>}
                        <div className="mt-2 text-slate-500 text-xs">Tip: Hold Ctrl/Cmd to select multiple.</div>
                      </div>

                      <div className="col-span-12 md:col-span-6">
                        <label className="inline-block mb-2">Status</label>
                        <FormSelect value={formData.status} onChange={(e) => handleInputChange("status", e.target.value)}>
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </FormSelect>
                      </div>
                    </div>

                    <div className="flex justify-between mt-8 gap-3">
                      <Button type="button" variant="outline-secondary" onClick={() => navigate(-1)}>
                        <Lucide icon="ArrowLeft" className="w-4 h-4 mr-2" />
                        Back
                      </Button>
                      <div className="flex gap-3">
                        <Button type="button" variant="outline-secondary" onClick={handleCancel} disabled={loading}>
                          Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                          {loading && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                          {mode === "add" ? "Create Distributor" : "Update Distributor"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

